import { Capacitor } from '@capacitor/core'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { PREFERS_WEB_STORAGE_KEY } from '../../config/storage.config'
import { useChallengeAccess } from '../../hooks/membership/useChallengeAccess'
import { useTeam } from '../../hooks/team/useTeam'
import { challengeTeamPath, ScreenPath } from '../../navigation'
import styles from '../../ui/onboarding/GuardView.module.css'
import DownloadAppView from './DownloadAppView'
import LoadingIndicator from '../../ui/util/LoadingIndicator'

export interface TeamRequiredViewProps {
  children: JSX.Element
}

export default function TeamRequiredView({ children }: TeamRequiredViewProps) {
  const navigate = useNavigate()
  const {
    challengeId,
    teamID,
    isFreeAgent,
    isEntitled,
    hasAnyMembership,
    isLoading,
  } = useChallengeAccess()
  const { data: team, isLoading: isLoadingTeam } = useTeam(teamID)

  const isWeb = Capacitor.getPlatform() === 'web'
  const prefersWeb = localStorage.getItem(PREFERS_WEB_STORAGE_KEY) === 'true'
  const needsChallengePick = !hasAnyMembership && !isEntitled && !challengeId
  const needsTeamIntro = !isFreeAgent && !teamID && !!challengeId
  const staleMembership = !!teamID && !isLoadingTeam && !team && !isFreeAgent

  useEffect(() => {
    if (isLoading) return
    if (needsChallengePick) {
      navigate(ScreenPath.challenges, { replace: true })
      return
    }
    if (needsTeamIntro || (staleMembership && challengeId)) {
      navigate(challengeTeamPath(challengeId), { replace: true })
    }
  }, [
    isLoading,
    needsChallengePick,
    needsTeamIntro,
    staleMembership,
    challengeId,
    navigate,
  ])

  if (isWeb && !prefersWeb) {
    return (
      <DownloadAppView
        onContinueWithWeb={() => {
          localStorage.setItem(PREFERS_WEB_STORAGE_KEY, 'true')
          navigate(ScreenPath.home)
        }}
      />
    )
  }

  if (isLoading || (!challengeId && hasAnyMembership)) {
    return (
      <div className={styles.loading}>
        <LoadingIndicator />
      </div>
    )
  }

  if (needsChallengePick || needsTeamIntro || staleMembership) {
    return (
      <div className={styles.loading}>
        <LoadingIndicator />
      </div>
    )
  }

  if (isFreeAgent) return children

  if (isLoadingTeam) {
    return (
      <div className={styles.loading}>
        <LoadingIndicator />
      </div>
    )
  }

  if (team) return children

  return (
    <div className={styles.loading}>
      <LoadingIndicator />
    </div>
  )
}
